import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';
import { participantApiService } from '@/services/participantApi';
import type { LoginRequest, SignupRequest, Participant } from '@shared/types';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<Omit<Participant, 'passwordHash'> | null>(null);
  const token = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isParticipant = computed(() => user.value?.role === 'participant');

  // Actions
  function initializeAuth() {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      token.value = savedToken;
      try {
        user.value = JSON.parse(savedUser);
      } catch (err) {
        clearAuth();
      }
    }
  }

  async function login(credentials: LoginRequest) {
    loading.value = true;
    error.value = null;

    try {
      const response = await participantApiService.login(credentials);

      token.value = response.token;
      user.value = response.user;

      // Persist to localStorage
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      return { success: true };
    } catch (err) {
      const errorMessage = participantApiService.extractErrorMessage(err);
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  async function signup(userData: SignupRequest) {
    loading.value = true;
    error.value = null;

    try {
      await participantApiService.signup(userData);
      return { success: true };
    } catch (err) {
      const errorMessage = participantApiService.extractErrorMessage(err);
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      loading.value = false;
    }
  }

  function logout() {
    clearAuth();
    // Redirect handled by router guard
  }

  function clearAuth() {
    user.value = null;
    token.value = null;
    error.value = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Close WebSocket connection
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    user: readonly(user),
    token: readonly(token),
    loading: readonly(loading),
    error: readonly(error),

    // Getters
    isAuthenticated,
    isAdmin,
    isParticipant,

    // Actions
    initializeAuth,
    login,
    signup,
    logout,
    clearAuth,
    clearError,
  };
});
